import {
  // requests
  CustodianServiceOptions,
  GetBankAccountIdentityRequestsForClUserOptions,
  GetAllBankAccountsForClUserOptions,
  GetMicroDepositOptions,
  GetLinkedCustodianAccountsForClUserOptions,
  GetLinkedBankAccountsForClUserOptions,
  GetTransferAmountLimitRequest,
  GetTransferMoneyRequestOptions,
  DeleteBankOptions,
  LinkedCustodianAccount,
  CustodianService,
  GetInfoOptions,
  SkipTransferOptions,
  SkipTransfer,
  ResumeTransferOptions,
  CancelTransferOptions,
  TransferMoneyRequestOptions,
  UpdateTransferMoneyRequestOptions,
  NextBusinessDateOptions,
  DeleteMoneyTransferRequestOptions,
  GetEsignDocumentOptions,
  DocuSignDocumentResponse,
  GetAutomatedAccountTransfersOptions,
  UpdateAutomatedAccountTransfersOptions,
  SaveAcatAggregatorHoldingsOptions,
  GetAdvisorAutomatedAccountTransfersOptions,
  GetBeneficiariesForClUserIdOptions,
  GetTrustedContactOptions,
  GetCustodianAccountsOptions,
  GetAllBankAccountLinkRequestsOptions,
  BatchOfBankAccountLinkRequestsOptions,
  GetClientNetWorthsOptions,
  GetClientIncomeIntervalsOptions,
  GetClientProfessionTypesOptions,
  GetClientRelationshipsOptions,
  AddAccountOptions,
  GetAccountAdditionalInfoLineOptions,
  GetSharedRepCodesOptions,
  UpdateAccountAdditionalInfoLineOptions,
  GetAccountRequestOptions,
  ClientDisclosuresOptions,
  DeleteBeneficiariesOptions,
  RemoveTrustedContactOptions,
  GetBeneficiaryByIdOptions,
  PutKycOptions,
  GetCustodianTransferFrequencyOptions,
  AccountTypeContributionLimitOptions,
  DefaultAdvisorRequestOptions,
  GetAcatsInstitutionsOptions,
  RequestStatusCountOptions,
  GetBankAccountIdentityRequestOptions,
  UpdateBankAccountIdentityRequestOptions,
  UpdateBankAccountIdentityDocumentRequestOptions,
  // responses
  DailyTransferLimitResponse,
  BusinessDateResponse,
  LatestCustodianLoadDate,
  UsState,
  AutomatedAccountTransferStatus,
  AcatRequestHoldingsSummary,
  Beneficiary,
  TrustedContact,
  BankAccountLinkRequest,
  AdditionalInfoHeader,
  ClientNetWorths,
  ClientIncomeIntervals,
  ClientProfessionTypes,
  ClientRelationships,
  Citizenships,
  AccountAdditionalInfo,
  SharedRepCodes,
  AccountRequest,
  Disclosures,
  DeleteAccountRequestHeaderOptions,
  TransferFrequencyObject,
  AccountTypeContributionLimit,
  TransferTypeResponse,
  TransferStatusResponse,
  IraDistributionReasonResponse,
  AcatsInstitutionResponse,
  BankAccountIdentityResponse,
  EmploymentData,
  GetEmploymentDataOptions,
  InvestmentProfileRecord,
  AccountRequestClientCompletions,
  AccountRequestClientCompletionsOptions,
} from '@digital-advice/ms-custodian-sdk';
import {DefaultRequestOptions, MicroserviceResponse} from '@digital-advice/ms-core-sdk';
import {apiKey, getLogger, endpoint, interceptors, sourceInfo} from '@advisor/services/sdk';

export const defaultOptions: CustodianServiceOptions = {
  logger: getLogger(),
  apiKey,
  endpoint,
  interceptors,
  sourceInfo,
};

export default class CustodianServices {
  static async sdkService(options: CustodianServiceOptions = defaultOptions): Promise<CustodianService> {
    const service = await new CustodianService(options);

    return service;
  }

  static async sdkServiceVersioning(apiVersion): Promise<CustodianService> {
    const service = await new CustodianService({
      logger: getLogger(),
      apiKey,
      endpoint,
      interceptors,
      apiVersion,
      sourceInfo,
    });

    return service;
  }

  /**
   * Gateway endpoint: getBankAccountIdentityRequestsForClUser
   * @param payload - A {@link GetBankAccountIdentityRequestsForClUserOptions} object.
   */
  static async getBankAccountIdentityRequestsForClUser(
    payload: GetBankAccountIdentityRequestsForClUserOptions
  ): Promise<unknown> {
    const sdkService = await this.sdkService();

    return sdkService.getBankAccountIdentityRequestsForClUser(payload);
  }

  /**
   * Gateway endpoint: getAllBankAccountsForClUser
   * @param payload - A {@link GetAllBankAccountsForClUserOptions} object.
   */
  static async getAllBankAccountsForClUser(payload: GetAllBankAccountsForClUserOptions): Promise<unknown> {
    const sdkService = await this.sdkService();

    return sdkService.getAllBankAccountsForClUser(payload);
  }

  /**
   * Gateway endpoint: getMicroDeposit
   * @param payload - A {@link GetBankAccountIdentityRequestsForClUserOptions} object.
   */
  static async getMicroDeposit(payload: GetMicroDepositOptions): Promise<unknown> {
    const sdkService = await this.sdkService();

    return sdkService.getMicroDeposit(payload);
  }

  /**
   * Gateway endpoint: getLinkedCustodianAccountsForClUser
   * @param payload - A {@link GetLinkedCustodianAccountsForClUserOptions} object.
   */
  static async getLinkedCustodianAccountsForClUser(
    payload: GetLinkedCustodianAccountsForClUserOptions
  ): Promise<LinkedCustodianAccount[]> {
    const sdkService = await this.sdkService();

    return sdkService.getLinkedCustodianAccountsForClUser(payload);
  }

  /**
   * Gateway endpoint: getLinkedBankAccountsForClUser
   * @param payload - A {@link GetLinkedBankAccountsForClUserOptions} object.
   */
  static async getLinkedBankAccountsForClUser(
    payload: GetLinkedBankAccountsForClUserOptions
  ): Promise<LinkedCustodianAccount[]> {
    const sdkService = await this.sdkService();

    return sdkService.getLinkedBankAccountsForClUser(payload);
  }

  /**
   * Gateway endpoint: deleteBankByBankAccountId
   * @param payload - A {@link DeleteBankOptions} object.
   */
  static async deleteBankByBankAccountId(payload: DeleteBankOptions): Promise<void> {
    const sdkService = await this.sdkService();

    return sdkService.deleteBankByBankAccountId(payload);
  }

  /**
   * Gateway endpoint: dailyTransferAmountLimit
   * @param payload - A {@link GetTransferAmountLimitRequest} object.
   */
  static async dailyTransferAmountLimit(payload: GetTransferAmountLimitRequest): Promise<DailyTransferLimitResponse> {
    const sdkService = await this.sdkService();

    return sdkService.dailyTransferAmountLimit(payload);
  }

  /**
   * Gateway endpoint: getTransferMoneyRequests
   * @param payload - A {@link GetTransferMoneyRequestOptions} object.
   */
  static async getTransferMoneyRequests(payload: GetTransferMoneyRequestOptions): Promise<unknown> {
    const sdkService = await this.sdkService();

    return sdkService.getTransferMoneyRequests(payload);
  }

  /**
   * Gateway endpoint: getInfo
   * @param payload - A {@link GetInfoOptions} object.
   */
  static async getCustodianInfo(payload: GetInfoOptions): Promise<unknown> {
    const sdkService = await this.sdkService();

    return sdkService.getInfo(payload);
  }

  /**
   * Gateway endpoint: transferMoneyRequest
   * @param payload - A {@link TransferMoneyRequestOptions} object.
   * @returns A promise of a {@link unknown} object.
   * RW-SDK version @since 5.72.3
   */
  static async transferMoneyRequest(payload: TransferMoneyRequestOptions): Promise<unknown> {
    const custodianService = await this.sdkService();

    return custodianService.transferMoneyRequest(payload);
  }

  /**
   * Gateway endpoint: updateMoneyTransferRequest
   * @param payload - A {@link UpdateTransferMoneyRequestOptions} object.
   * @returns A promise of a {@link unknown} object.
   * RW-SDK version @since 5.72.3
   */
  static async updateMoneyTransferRequest(payload: UpdateTransferMoneyRequestOptions): Promise<unknown> {
    const custodianService = await this.sdkService();

    return custodianService.updateMoneyTransferRequest(payload);
  }

  /**
   * Gateway endpoint: saveSkipTransfer
   * @param payload - A {@link SkipTransferOptions} object.
   * @returns A promise of a {@link SkipTransfer} object.
   * RW-SDK version @since 5.72.3
   */
  static async saveSkipTransfer(payload: SkipTransferOptions): Promise<SkipTransfer> {
    const apiVersion = '20210716';
    const custodianService = await this.sdkServiceVersioning(apiVersion);

    return custodianService.saveSkipTransfer(payload);
  }

  /**
   * Gateway endpoint: resumeTransfers
   * @param payload - A {@link ResumeTransferOptions} object.
   * @returns A promise of a {@link SkipTransfer} object.
   * RW-SDK version @since 5.72.3
   */
  static async resumeTransfers(payload: ResumeTransferOptions): Promise<SkipTransfer> {
    const custodianService = await this.sdkService();

    return custodianService.resumeTransfers(payload);
  }

  /**
   * Gateway endpoint: cancelTransfer
   * @param payload - A {@link CancelTransferOptions} object.
   * @returns A promise of a {@link unknown} object.
   * RW-SDK version @since 5.72.3
   */
  static async cancelTransfer(payload: CancelTransferOptions): Promise<unknown> {
    const custodianService = await this.sdkService();

    return custodianService.cancelTransfer(payload);
  }

  /**
   * Gateway endpoint: getNextBusinessDate
   * @param payload - A {@link NextBusinessDateOptions} object.
   */
  static async getNextBusinessDate(payload: NextBusinessDateOptions): Promise<BusinessDateResponse> {
    const custodianService = await this.sdkService();

    return custodianService.getNextBusinessDate(payload);
  }

  /**
   * Gateway endpoint: deleteMoneyTransferRequest
   * @param payload - A {@link DeleteMoneyTransferRequestOptions} object.
   */
  static async deleteMoneyTransferRequest(payload: DeleteMoneyTransferRequestOptions): Promise<unknown> {
    const custodianService = await this.sdkService();

    return custodianService.deleteMoneyTransferRequest(payload);
  }

  /**
   * Gateway endpoint: getEsignDocument
   * @param payload - A {@link GetEsignDocumentOptions} object.
   */
  static async getEsignDocument(payload: GetEsignDocumentOptions): Promise<DocuSignDocumentResponse> {
    const custodianService = await this.sdkService();

    return custodianService.getEsignDocument(payload);
  }

  /**
   * Gateway endpoint: getLatestCustodianLoadDate
   * @param payload - A {@link DefaultRequestOptions} object.
   */
  static async getLatestCustodianLoadDate(payload: DefaultRequestOptions): Promise<LatestCustodianLoadDate[]> {
    const custodianService = await this.sdkService();

    return custodianService.getLatestCustodianLoadDate(payload);
  }

  /**
   * Gateway endpoint: getClientAutomatedAccountTransfers
   * @param payload - A {@link GetAutomatedAccountTransfersOptions} object.
   */
  static async getClientAutomatedAccountTransfers(
    payload: GetAutomatedAccountTransfersOptions
  ): Promise<AutomatedAccountTransferStatus[]> {
    const custodianService = await this.sdkService();

    return custodianService.getClientAutomatedAccountTransfers(payload);
  }

  /**
   * Gateway endpoint: getAdvisorAutomatedAccountTransfers
   * @param payload - A {@link GetAdvisorAutomatedAccountTransfersOptions} object.
   */
  static async getAdvisorAutomatedAccountTransfers(
    payload: GetAdvisorAutomatedAccountTransfersOptions
  ): Promise<AutomatedAccountTransferStatus[]> {
    const custodianService = await this.sdkService();

    return custodianService.getAdvisorAutomatedAccountTransfers(payload);
  }

  /**
   * Gateway endpoint: updateClientAutomatedAccountTransfers
   * @param payload - A {@link UpdateAutomatedAccountTransfersOptions} object.
   */
  static async updateClientAutomatedAccountTransfers(
    payload: UpdateAutomatedAccountTransfersOptions
  ): Promise<AutomatedAccountTransferStatus> {
    const custodianService = await this.sdkService();

    return custodianService.updateClientAutomatedAccountTransfers(payload);
  }

  /**
   * Gateway endpoint: saveAutomatedAccountTransferHoldings
   * @param payload - A {@link DefaultRequestOptions} object.
   */
  static async saveAutomatedAccountTransferHoldings(
    payload: SaveAcatAggregatorHoldingsOptions
  ): Promise<AcatRequestHoldingsSummary> {
    const custodianService = await this.sdkService();

    return custodianService.saveAutomatedAccountTransferHoldings(payload);
  }

  /**
   * Gateway endpoint: getUsStates
   * @param payload - A {@link DefaultRequestOptions} object.
   */
  static async getUsStates(payload: DefaultRequestOptions): Promise<UsState[]> {
    const custodianService = await this.sdkService();

    return custodianService.getUsStates(payload);
  }

  /**
   * Gateway endpoint: getBeneficiariesForClUserId
   * @param payload - A {@link DefaultRequestOptions} object.
   */
  static async getBeneficiaries(payload: GetBeneficiariesForClUserIdOptions): Promise<Beneficiary[]> {
    const custodianService = await this.sdkService();

    return custodianService.getBeneficiariesForClUserId(payload);
  }

  /**
   * Gateway endpoint: custodianAccounts/trustedContact?clUserId
   * @param payload - A {@link DefaultRequestOptions} object.
   */
  static async getTrustedContact(payload: GetTrustedContactOptions): Promise<TrustedContact[]> {
    const custodianService = await this.sdkService();

    return custodianService.getTrustedContact(payload);
  }

  /**
   * @param payload - A {@link GetCustodianAccountsOptions} object.
   */
  static async getCustodianAccounts(payload: GetCustodianAccountsOptions): Promise<unknown> {
    const custodianService = await this.sdkService();

    return custodianService.getCustodianAccounts(payload);
  }

  /**
   * @param payload - A {@link GetCustodianAccountsOptions} object.
   */
  static async getAllBankAccountLinkRequests(
    payload: GetAllBankAccountLinkRequestsOptions
  ): Promise<BankAccountLinkRequest[]> {
    const custodianService = await this.sdkService();

    return custodianService.getAllBankAccountLinkRequests(payload);
  }

  /**
   * @param payload - A {@link BatchOfBankAccountLinkRequestsOptions} object.
   */
  static async postBankAccountLinkRequests(
    payload: BatchOfBankAccountLinkRequestsOptions
  ): Promise<BankAccountLinkRequest[]> {
    const custodianService = await this.sdkService();

    return custodianService.postBankAccountLinkRequests(payload);
  }

  /**
   * Get account additional info header.
   *
   * @async
   * @param options - A {@link DefaultRequestOptions} object.
   * @returns A promise of a list of {@link AdditionalInfoHeader} objects.
   */

  static async getAdditionalInfoHeader(payload: DefaultRequestOptions): Promise<AdditionalInfoHeader[]> {
    const custodianService = await this.sdkService();

    return custodianService.getAdditionalInfoHeader(payload);
  }

  /**
   * Get client net worths.
   *
   * @async
   * @param options - A {@link GetClientNetWorthsOptions} object.
   * @returns A promise of a list of {@link ClientNetWorths} objects.
   */
  static async getClientNetWorths(options: GetClientNetWorthsOptions): Promise<ClientNetWorths[]> {
    const custodianService = await this.sdkService();

    return custodianService.getClientNetWorths(options);
  }

  /**
   * Get client income intervals.
   *
   * @async
   * @param options - A {@link GetClientIncomeIntervalsOptions} object.
   * @returns A promise of a list of {@link ClientIncomeIntervals} objects.
   */
  static async getClientIncomeIntervals(options: GetClientIncomeIntervalsOptions): Promise<ClientIncomeIntervals[]> {
    const custodianService = await this.sdkService();

    return custodianService.getClientIncomeIntervals(options);
  }

  /**
   * Get client profession types.
   *
   * @async
   * @param options - A {@link GetClientProfessionTypesOptions} object.
   * @returns A promise of a list of {@link ClientProfessionTypes} objects.
   */
  static async getClientProfessionTypes(options: GetClientProfessionTypesOptions): Promise<ClientProfessionTypes[]> {
    const custodianService = await this.sdkService();

    return custodianService.getClientProfessionTypes(options);
  }

  /**
   * Get client relationships.
   *
   * @async
   * @param options - A {@link GetClientRelationshipsOptions} object.
   * @returns A promise of a list of {@link ClientRelationships} objects.
   */
  static async getClientRelationships(options: GetClientRelationshipsOptions): Promise<ClientRelationships[]> {
    const custodianService = await this.sdkService();

    return custodianService.getClientRelationships(options);
  }

  /**
   * Get client citizenships.
   *
   * @async
   * @param options - A {@link DefaultRequestOptions} object.
   * @returns A promise of a list of {@link Citizenships} objects.
   */
  static async getCitizenships(options: DefaultRequestOptions): Promise<Citizenships[]> {
    const custodianService = await this.sdkService();

    return custodianService.getCitizenships(options);
  }

  /**
   * Add a new account for a user.
   *
   * @async
   * @param options - A {@link AddAccountOptions} object.
   * @returns A promise.
   */
  static async addAccount(options: AddAccountOptions): Promise<unknown> {
    const custodianService = await this.sdkService();

    return custodianService.addAccount(options);
  }

  /**
   * Get account additional info line.
   *
   * @async
   * @param options - A {@link GetAccountAdditionalInfoLineOptions} object.
   * @returns A promise of a list of {@link AccountAdditionalInfo} objects.
   */
  static async getAccountAdditionalInfoLine(
    options: GetAccountAdditionalInfoLineOptions
  ): Promise<AccountAdditionalInfo[]> {
    const custodianService = await this.sdkService();

    return custodianService.getAccountAdditionalInfoLine(options);
  }

  /**
   * Get shared rep codes.
   *
   * @async
   * @param options - A {@link GetSharedRepCodesOptions} object.
   * @returns A promise of a list of {@link SharedRepCodes} objects.
   */
  static async getSharedRepCodes(options: GetSharedRepCodesOptions): Promise<SharedRepCodes[]> {
    const custodianService = await this.sdkService();

    return custodianService.getSharedRepCodes(options);
  }

  /**
   * Update account additional info line.
   *
   * @async
   * @param options - A {@link UpdateAccountAdditionalInfoLineOptions} object.
   * @returns A promise of a list of {@link AccountAdditionalInfo} objects.
   */
  static async updateAccountAdditionalInfoLine(
    options: UpdateAccountAdditionalInfoLineOptions
  ): Promise<AccountAdditionalInfo[]> {
    const custodianService = await this.sdkService();

    return custodianService.updateAccountAdditionalInfoLine(options);
  }

  /**
   * Add a new account for a user.
   *
   * @async
   * @param options - A {@link GetAccountRequestOptions} object.
   * @returns A promise.
   */
  static async getAccountRequest(options: GetAccountRequestOptions): Promise<AccountRequest> {
    const custodianService = await this.sdkService();

    return custodianService.getAccountRequest(options);
  }

  /*
   * Get client disclosures.
   *
   * @async
   * @param options - A {@link ClientDisclosuresOptions} object.
   * @returns A promise of a {@link Disclosures} object.
   */
  static async getClientDisclosures(options: ClientDisclosuresOptions): Promise<Disclosures> {
    const custodianService = await this.sdkService();

    return custodianService.getClientDisclosures(options);
  }

  /*
   * Deletes Beneficiaries.
   *
   * @async
   * @param options - A {@link DeleteBeneficiariesOptions} object.
   * @returns A promise.
   */
  static async deleteBeneficiaries(options: DeleteBeneficiariesOptions): Promise<void> {
    const custodianService = await this.sdkService();

    return custodianService.deleteBeneficiaries(options);
  }

  /*
   * Deletes a trusted contact.
   *
   * @async
   * @param options - A {@link RemoveTrustedContactOptions} object.
   * @returns A promise.
   */
  static async removeTrustedContact(options: RemoveTrustedContactOptions): Promise<void> {
    const custodianService = await this.sdkService();

    return custodianService.removeTrustedContact(options);
  }

  /*
   * Get Beneficiary by its Beneficiary ID.
   *
   * @async
   * @param options - A {@link GetBeneficiaryByIdOptions} object.
   * @returns A promise of a {@link Beneficiary} object.
   */
  static async getBeneficiary(options: GetBeneficiaryByIdOptions): Promise<Beneficiary> {
    const custodianService = await this.sdkService();

    return custodianService.getBeneficiaryByBeneficiaryId(options);
  }

  /*
   * Get transfer frequency
   * @param options - A {@link GetCustodianTransferFrequencyOptions} object.
   * @returns A promise of a {@link TransferFrequencyObject} object.
   */
  static async getCustodianTransferFrequency(
    options: GetCustodianTransferFrequencyOptions
  ): Promise<TransferFrequencyObject[]> {
    const custodianService = await this.sdkService();

    return custodianService.getCustodianTransferFrequency(options);
  }

  /*
   * Get account type contribution limit by account type and contribution year
   * @param options - A {@link AccountTypeContributionLimitOptions} object.
   * @returns A promise of a {@link AccountTypeContributionLimit} object.
   */
  static async getAccountTypeContributionLimit(
    options: AccountTypeContributionLimitOptions
  ): Promise<AccountTypeContributionLimit[]> {
    const custodianService = await this.sdkService();

    return custodianService.getAccountTypeContributionLimit(options);
  }

  /**
   * Update KYC Status.
   *
   * @async
   * @param options - A {@link PutKycOptions} object.
   * @returns A promise.
   */
  static async putKycStatus(options: PutKycOptions): Promise<void> {
    const custodianService = await this.sdkService();

    return custodianService.putKycStatus(options);
  }

  /**
   * cancel the AccountRequestHeader by accountRequestId.
   *
   * @async
   * @param options - A {@link DeleteAccountRequestHeaderOptions} object.
   * @returns A promise.
   */
  static async cancelAccountRequestHeader(options: DeleteAccountRequestHeaderOptions): Promise<void> {
    const custodianService = await this.sdkService();

    return custodianService.cancelAccountRequestHeader(options);
  }

  /**
   * Statuswise count of Bank Account Identity Request
   *
   * @async
   * @param options - A {@link RequestStatusCountOptions} object.
   * @returns A promise.
   */

  static async getStatusWiseCountOfBankAccountIdentityRequests(
    options: RequestStatusCountOptions
  ): Promise<Map<string, number>> {
    const custodianService = await this.sdkService();

    return custodianService.getStatusWiseCountOfBankAccountIdentityRequests(options);
  }

  /*
   * Get transfer types
   * @param options - A {@link DefaultAdvisorRequestOptions} object.
   * @returns A promise of an array of {@link TransferTypeResponse} objects.
   */
  static async getTransferTypes(options: DefaultAdvisorRequestOptions): Promise<TransferTypeResponse[]> {
    const custodianService = await this.sdkService();

    return custodianService.getTransferTypes(options);
  }

  /*
   * Get transfer status
   * @param options - A {@link DefaultAdvisorRequestOptions} object.
   * @returns A promise of an array of {@link TransferStatusResponse} objects.
   */
  static async getTransferStatus(options: DefaultAdvisorRequestOptions): Promise<TransferStatusResponse[]> {
    const custodianService = await this.sdkService();

    return custodianService.getTransferStatus(options);
  }

  /*
   * Get Ira Distribution Reasons
   * @param options - A {@link DefaultAdvisorRequestOptions} object.
   * @returns A promise of an array of {@link IraDistributionReasonResponse} objects.
   */
  static async getIraDistributionReasons(options: DefaultAdvisorRequestOptions): Promise<IraDistributionReasonResponse[]> {
    const custodianService = await this.sdkService();

    return custodianService.getIraDistributionReasons(options);
  }

  /*
   * Get acats institutions
   * @param options - A {@link GetAcatsInstitutionsOptions} object.
   * @returns A promise of an array of {@link AcatsInstitutionResponse} objects.
   */
  static async getAcatsInstitutions(options: GetAcatsInstitutionsOptions): Promise<AcatsInstitutionResponse[]> {
    const custodianService = await this.sdkService();

    return custodianService.getAcatsInstitutions(options);
  }

  /**
   * Get Bank Account Identity Request options
   *
   * @async
   * @param options - A {@link GetBankAccountIdentityRequestOptions} object.
   * @returns A promise.
   */
  static async getBankAccountIdentityRequestsForAdvisor(
    options: GetBankAccountIdentityRequestOptions
  ): Promise<MicroserviceResponse<BankAccountIdentityResponse>> {
    const custodianService = await this.sdkService();

    return custodianService.getBankAccountIdentityRequestsForAdvisor(options);
  }

  /*
   * Updates a bank account link identity request.
   * @param options - A {@link UpdateBankAccountIdentityRequestOptions} object.
   * @returns A promise.
   */
  static async updateBankAccountIdentityRequest(options: UpdateBankAccountIdentityRequestOptions): Promise<unknown> {
    const custodianService = await this.sdkService();

    return custodianService.updateBankAccountIdentityRequest(options);
  }

  /*
   * Updates a bank account link identity document request.
   * @param options - A {@link UpdateBankAccountIdentityDocumentRequestOptions} object.
   * @returns A promise.
   */
  static async updateBankAccountIdentityDocumentRequest(
    options: UpdateBankAccountIdentityDocumentRequestOptions
  ): Promise<unknown> {
    const custodianService = await this.sdkService();

    return custodianService.updateBankAccountIdentityDocumentRequest(options);
  }

  static async getEmploymentData(options: GetEmploymentDataOptions): Promise<EmploymentData> {
    const custodianService = await this.sdkService();

    return custodianService.getEmploymentData(options);
  }

  static async getDisclosures(clUserId: string): Promise<Disclosures> {
    const custodianService = await this.sdkService();

    return custodianService.getClientDisclosures({clUserId});
  }

  static async getInvestmentProfile(options): Promise<InvestmentProfileRecord> {
    const custodianService = await this.sdkService();

    return custodianService.getInvestmentProfileRecord(options);
  }

  static async updateAccountRequestCompletions(
    options: AccountRequestClientCompletionsOptions
  ): Promise<AccountRequestClientCompletions> {
    const custodianService = await this.sdkService();

    return custodianService.updateAccountRequestCompletions(options);
  }
}
